import { PixiAppRefs } from './types';

/** Name of the window-scoped test hook installed for end-to-end runs. */
const HOOK_NAME = '__harnessTest';

/** The surface exposed to end-to-end tests on `window.__harnessTest`. */
export interface HarnessTestHook {
  /**
   * Client-space centre of the row for the given wire on whichever connector
   * shows it, or null when the wire is not currently rendered. Lets a test
   * right-click the exact wire row despite the canvas being opaque to the DOM.
   */
  wireRowClientPoint(wireId: string): { x: number; y: number } | null;
}

/**
 * Installs {@link HarnessTestHook} on `window`, translating a wire id to the
 * screen point of its row via the live connector entities and viewport. Intended
 * only for test builds; the caller gates installation on a build-time flag so it
 * never ships in the normal app.
 */
export function installE2EHook(refs: PixiAppRefs): void {
  const wireRowClientPoint = (wireId: string): { x: number; y: number } | null => {
    const viewportManager = refs.viewportManager.current;
    const app = refs.appManager.current?.getApp();
    if (!viewportManager || !app) {
      return null;
    }
    const connectors = Array.from(refs.connectorEntities.current.values());
    for (const connector of connectors) {
      const local = connector.wireRowLocalCenter(wireId);
      if (!local) {
        continue;
      }
      const screen = viewportManager.worldToScreen(connector.x + local.x, connector.y + local.y);
      const rect = (app.canvas as HTMLCanvasElement).getBoundingClientRect();
      return { x: rect.left + screen.x, y: rect.top + screen.y };
    }
    return null;
  };

  (window as unknown as Record<string, HarnessTestHook>)[HOOK_NAME] = { wireRowClientPoint };
}
